import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, Paper, Grid, Card, CardContent,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  Tabs, Tab, Alert, CircularProgress, Divider, Chip, TextField,
  LinearProgress,
} from '@mui/material';
import { ArrowUpward, ArrowDownward, FiberManualRecord } from '@mui/icons-material';
import {
  fetchAnalyticsOverview, fetchRevenueTrend, fetchCustomerInsights,
  fetchProductPerformance, fetchOrderFunnel, fetchGeographicData, fetchRealTimeStats,
} from '../../api/api';

const PERIODS = ['7d', '30d', '90d', '1y'];
const PERIOD_LABELS = { '7d': '7 Days', '30d': '30 Days', '90d': '90 Days', '1y': '1 Year' };

function KpiCard({ label, value, prev, growth, prefix = '', suffix = '' }) {
  const up = growth >= 0;
  return (
    <Card sx={{ bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 3, height: '100%' }}>
      <CardContent>
        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 1, fontWeight: 700 }}>{label}</Typography>
        <Typography variant="h5" fontWeight={900} color="text.primary" sx={{ my: 0.5 }}>
          {prefix}{typeof value === 'number' ? value.toLocaleString() : (value ?? '-')}{suffix}
        </Typography>
        {growth !== undefined && growth !== null && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {up ? <ArrowUpward sx={{ color: 'success.main', fontSize: 16 }} /> : <ArrowDownward sx={{ color: 'error.main', fontSize: 16 }} />}
            <Typography variant="caption" sx={{ color: up ? 'success.main' : 'error.main', fontWeight: 700 }}>
              {Math.abs(growth).toFixed(1)}%
            </Typography>
            <Typography variant="caption" color="text.secondary">vs prev period</Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

export default function AdvancedAnalytics() {
  const [period, setPeriod] = useState('30d');
  const [overview, setOverview] = useState(null);
  const [revenue, setRevenue] = useState([]);
  const [customers, setCustomers] = useState(null);
  const [productPerf, setProductPerf] = useState({ topByRevenue: [], topByOrders: [], outOfStock: [] });
  const [funnel, setFunnel] = useState([]);
  const [geo, setGeo] = useState([]);
  const [realtime, setRealtime] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [productTab, setProductTab] = useState('revenue');
  const [trendStart, setTrendStart] = useState('');
  const [trendEnd, setTrendEnd] = useState('');
  const [rtLoading, setRtLoading] = useState(false);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [ov, rv, cu, pr, fn, ge] = await Promise.allSettled([
        fetchAnalyticsOverview(period),
        fetchRevenueTrend({ period, startDate: trendStart || undefined, endDate: trendEnd || undefined }),
        fetchCustomerInsights(),
        fetchProductPerformance({ period }),
        fetchOrderFunnel({ period }),
        fetchGeographicData(),
      ]);
      if (ov.status === 'fulfilled') setOverview(ov.value.data);
      if (rv.status === 'fulfilled') setRevenue(rv.value.data?.trend || rv.value.data || []);
      if (cu.status === 'fulfilled') setCustomers(cu.value.data);
      if (pr.status === 'fulfilled') {
        const d = pr.value.data || {};
        setProductPerf({
          topByRevenue: Array.isArray(d.topByRevenue) ? d.topByRevenue : [],
          topByOrders:  Array.isArray(d.topByOrders)  ? d.topByOrders  : [],
          outOfStock:   Array.isArray(d.outOfStock)   ? d.outOfStock   : [],
        });
      }
      if (fn.status === 'fulfilled') setFunnel(fn.value.data?.funnel || fn.value.data || []);
      if (ge.status === 'fulfilled') setGeo(ge.value.data?.areas || ge.value.data || []);
    } catch (e) {
      setError('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  }, [period, trendStart, trendEnd]);

  const loadRealtime = useCallback(async () => {
    setRtLoading(true);
    try {
      const res = await fetchRealTimeStats();
      setRealtime(res.data);
    } catch {}
    finally { setRtLoading(false); }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);
  useEffect(() => {
    loadRealtime();
    const interval = setInterval(loadRealtime, 30000);
    return () => clearInterval(interval);
  }, [loadRealtime]);

  const maxRevenue = Math.max(...revenue.map(r => Math.max(r.online || 0, r.pos || 0)), 1);
  const maxFunnel = funnel.length ? Math.max(...funnel.map(f => f.count || 0), 1) : 1;
  const normalizeProduct = (p) => ({
    _id:     p._id,
    name:    p.product?.title || p.title || p.name || '—',
    units:   p.unitsSold ?? p.units ?? p.quantity ?? 0,
    revenue: p.revenue || 0,
    stock:   p.stock,
  });
  const productSubsets = {
    revenue: productPerf.topByRevenue.map(normalizeProduct).slice(0, 10),
    orders:  productPerf.topByOrders.map(normalizeProduct).slice(0, 10),
    worst:   [...productPerf.topByRevenue].map(normalizeProduct).sort((a, b) => a.revenue - b.revenue).slice(0, 10),
    oos:     productPerf.outOfStock.map(normalizeProduct).slice(0, 10),
  };

  return (
    <Box sx={{ p: 3, bgcolor: 'background.default', minHeight: '100vh' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={800} color="text.primary">Advanced Analytics</Typography>
          <Typography variant="body2" color="text.secondary">Business intelligence dashboard</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {PERIODS.map(p => (
            <Button key={p} variant={period === p ? 'contained' : 'outlined'} size="small" onClick={() => setPeriod(p)}
              sx={period === p ? { background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', fontWeight: 700 } : { fontWeight: 600 }}>
              {PERIOD_LABELS[p]}
            </Button>
          ))}
        </Box>
      </Box>

      {loading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {!loading && (
        <>
          {/* Section 1 - KPI Cards */}
          <Grid container spacing={2} sx={{ mb: 4 }}>
            {[
              { label: 'Total Revenue', value: overview?.revenue?.total, prefix: 'KES ', growth: overview?.revenue?.growth },
              { label: 'Total Orders', value: overview?.orders?.total, growth: overview?.orders?.growth },
              { label: 'Avg Order Value', value: overview?.orders?.avgValue, prefix: 'KES ', growth: overview?.orders?.avgGrowth },
              { label: 'Total Customers', value: overview?.customers?.total, growth: null },
              { label: 'New Customers', value: overview?.customers?.new, growth: overview?.customers?.newGrowth },
              { label: 'Active Riders', value: overview?.riders?.active, growth: null },
            ].map((kpi, i) => (
              <Grid item xs={6} md={4} lg={2} key={i}>
                <KpiCard {...kpi} />
              </Grid>
            ))}
          </Grid>

          {/* Section 2 - Revenue Trend */}
          <Paper sx={{ p: 3, mb: 4, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 2 }}>
              <Typography variant="h6" fontWeight={800} color="text.primary">Revenue Trend</Typography>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <TextField label="Start Date" type="date" size="small" value={trendStart}
                  onChange={e => setTrendStart(e.target.value)} InputLabelProps={{ shrink: true }} />
                <TextField label="End Date" type="date" size="small" value={trendEnd}
                  onChange={e => setTrendEnd(e.target.value)} InputLabelProps={{ shrink: true }} />
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><Box sx={{ width: 12, height: 12, borderRadius: 1, bgcolor: '#667eea' }} /><Typography variant="caption">Online</Typography></Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><Box sx={{ width: 12, height: 12, borderRadius: 1, bgcolor: '#10b981' }} /><Typography variant="caption">POS</Typography></Box>
            </Box>
            {revenue.length === 0 ? (
              <Typography color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>No revenue data for this period</Typography>
            ) : (
              <>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 2 }}>
                  {revenue.slice(-14).map((r, i) => (
                    <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ width: 80, flexShrink: 0 }}>
                        {r.date ? new Date(r.date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : `Day ${i + 1}`}
                      </Typography>
                      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.3 }}>
                        <Box sx={{ height: 10, borderRadius: 1, bgcolor: '#667eea', width: `${((r.online || 0) / maxRevenue) * 100}%`, minWidth: 2 }} />
                        <Box sx={{ height: 10, borderRadius: 1, bgcolor: '#10b981', width: `${((r.pos || 0) / maxRevenue) * 100}%`, minWidth: 2 }} />
                      </Box>
                      <Typography variant="caption" color="text.secondary" sx={{ width: 80, textAlign: 'right', flexShrink: 0 }}>
                        {((r.online || 0) + (r.pos || 0)).toLocaleString()}
                      </Typography>
                    </Box>
                  ))}
                </Box>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Date</TableCell>
                        <TableCell align="right">Online (KES)</TableCell>
                        <TableCell align="right">POS (KES)</TableCell>
                        <TableCell align="right">Total (KES)</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {revenue.slice(-7).map((r, i) => (
                        <TableRow key={i}>
                          <TableCell>{r.date ? new Date(r.date).toLocaleDateString() : `Entry ${i + 1}`}</TableCell>
                          <TableCell align="right">{(r.online || 0).toLocaleString()}</TableCell>
                          <TableCell align="right">{(r.pos || 0).toLocaleString()}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700 }}>{((r.online || 0) + (r.pos || 0)).toLocaleString()}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}
          </Paper>

          {/* Section 3 - Funnel + Geo */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 3, height: '100%' }}>
                <Typography variant="h6" fontWeight={800} color="text.primary" sx={{ mb: 2 }}>Order Funnel</Typography>
                {funnel.length === 0 ? (
                  <Typography color="text.secondary">No funnel data</Typography>
                ) : (
                  funnel.map((stage, i) => (
                    <Box key={i} sx={{ mb: 1.5 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="body2" fontWeight={600} color="text.primary">{stage.stage || stage.name}</Typography>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                          <Typography variant="body2" color="text.primary">{(stage.count || 0).toLocaleString()}</Typography>
                          {i > 0 && stage.dropOff !== undefined && (
                            <Chip label={`-${stage.dropOff}%`} size="small" color="error" sx={{ height: 18, fontSize: 10 }} />
                          )}
                        </Box>
                      </Box>
                      <LinearProgress variant="determinate" value={Math.min(((stage.count || 0) / maxFunnel) * 100, 100)}
                        sx={{ height: 10, borderRadius: 2, bgcolor: 'action.hover', '& .MuiLinearProgress-bar': { borderRadius: 2, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' } }} />
                      {stage.value !== undefined && (
                        <Typography variant="caption" color="text.secondary">KES {(stage.value || 0).toLocaleString()}</Typography>
                      )}
                    </Box>
                  ))
                )}
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 3, height: '100%' }}>
                <Typography variant="h6" fontWeight={800} color="text.primary" sx={{ mb: 2 }}>Geographic Breakdown</Typography>
                {geo.length === 0 ? (
                  <Typography color="text.secondary">No geographic data</Typography>
                ) : (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Area</TableCell>
                          <TableCell align="right">Orders</TableCell>
                          <TableCell align="right">Revenue (KES)</TableCell>
                          <TableCell align="right">% of Total</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {geo.map((area, i) => (
                          <TableRow key={i}>
                            <TableCell>{area.area || area.name}</TableCell>
                            <TableCell align="right">{(area.orders || 0).toLocaleString()}</TableCell>
                            <TableCell align="right">{(area.revenue || 0).toLocaleString()}</TableCell>
                            <TableCell align="right">{area.percentage || area.pct || 0}%</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Paper>
            </Grid>
          </Grid>

          {/* Section 4 - Product Performance */}
          <Paper sx={{ p: 3, mb: 4, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
            <Typography variant="h6" fontWeight={800} color="text.primary" sx={{ mb: 2 }}>Product Performance</Typography>
            <Tabs value={productTab} onChange={(_, v) => setProductTab(v)} sx={{ mb: 2 }}>
              <Tab value="revenue" label="Top by Revenue" />
              <Tab value="orders" label="Top by Orders" />
              <Tab value="worst" label="Worst Performers" />
              <Tab value="oos" label="Out of Stock" />
            </Tabs>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Rank</TableCell>
                    <TableCell>Product</TableCell>
                    <TableCell align="right">Units</TableCell>
                    <TableCell align="right">Revenue (KES)</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(productSubsets[productTab] || []).length === 0 ? (
                    <TableRow><TableCell colSpan={4} align="center"><Typography color="text.secondary">No data</Typography></TableCell></TableRow>
                  ) : (
                    (productSubsets[productTab] || []).map((p, i) => (
                      <TableRow key={p._id || i}>
                        <TableCell>
                          <Chip label={`#${i + 1}`} size="small" sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: '#fff', fontWeight: 700 }} />
                        </TableCell>
                        <TableCell>{p.name || p.product}</TableCell>
                        <TableCell align="right">{(p.units || p.quantity || 0).toLocaleString()}</TableCell>
                        <TableCell align="right">{(p.revenue || 0).toLocaleString()}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          {/* Section 5 - Customer Insights */}
          <Paper sx={{ p: 3, mb: 4, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
            <Typography variant="h6" fontWeight={800} color="text.primary" sx={{ mb: 2 }}>Customer Insights</Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={8}>
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Top 10 Customers</Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Email</TableCell>
                        <TableCell align="right">Total Spent (KES)</TableCell>
                        <TableCell align="right">Orders</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(customers?.topCustomers || []).length === 0 ? (
                        <TableRow><TableCell colSpan={4} align="center"><Typography color="text.secondary">No data</Typography></TableCell></TableRow>
                      ) : (
                        (customers?.topCustomers || []).slice(0, 10).map((c, i) => (
                          <TableRow key={i}>
                            <TableCell>{c.name}</TableCell>
                            <TableCell>{c.email}</TableCell>
                            <TableCell align="right">{(c.totalSpent || 0).toLocaleString()}</TableCell>
                            <TableCell align="right">{c.orders || 0}</TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 2 }}>New vs Returning</Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, borderRadius: 2, bgcolor: 'rgba(102,126,234,0.08)', border: '1px solid rgba(102,126,234,0.2)' }}>
                    <Box sx={{ width: 16, height: 16, borderRadius: '50%', bgcolor: '#667eea', flexShrink: 0 }} />
                    <Box>
                      <Typography fontWeight={800} color="text.primary">{(customers?.newCustomers || 0).toLocaleString()}</Typography>
                      <Typography variant="caption" color="text.secondary">New Customers</Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, borderRadius: 2, bgcolor: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.2)' }}>
                    <Box sx={{ width: 16, height: 16, borderRadius: '50%', bgcolor: '#10b981', flexShrink: 0 }} />
                    <Box>
                      <Typography fontWeight={800} color="text.primary">{(customers?.returningCustomers || 0).toLocaleString()}</Typography>
                      <Typography variant="caption" color="text.secondary">Returning Customers</Typography>
                    </Box>
                  </Box>
                  {customers?.retentionRate !== undefined && (
                    <Box sx={{ p: 2, borderRadius: 2, bgcolor: 'action.hover' }}>
                      <Typography variant="caption" color="text.secondary">Retention Rate</Typography>
                      <Typography fontWeight={800} color="text.primary">{customers.retentionRate}%</Typography>
                    </Box>
                  )}
                </Box>
              </Grid>
            </Grid>
          </Paper>

          {/* Section 6 - Real-time */}
          <Paper sx={{ p: 3, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <FiberManualRecord sx={{ color: '#10b981', fontSize: 16, animation: 'pulse 2s infinite', '@keyframes pulse': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0.4 } } }} />
              <Typography variant="h6" fontWeight={800} color="text.primary">Real-time Stats</Typography>
              <Typography variant="caption" color="text.secondary">(refreshes every 30s)</Typography>
              {rtLoading && <CircularProgress size={14} />}
            </Box>
            <Grid container spacing={2}>
              {[
                { label: 'Active Users', value: realtime?.activeUsers },
                { label: 'Pending Orders', value: realtime?.pendingOrders },
                { label: 'Active Riders', value: realtime?.activeRiders },
                { label: "Today's Revenue", value: realtime?.todayRevenue, prefix: 'KES ' },
                { label: "Today's Orders", value: realtime?.todayOrders },
              ].map((m, i) => (
                <Grid item xs={6} sm={4} md={2.4} key={i}>
                  <Box sx={{ p: 2, borderRadius: 2, bgcolor: 'action.hover', textAlign: 'center' }}>
                    <Typography variant="h5" fontWeight={900} color="text.primary">
                      {m.prefix || ''}{m.value !== undefined && m.value !== null ? (typeof m.value === 'number' ? m.value.toLocaleString() : m.value) : '-'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">{m.label}</Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Paper>
        </>
      )}
    </Box>
  );
}
